"use client";

import { TGetCategoryDataResponseDTO } from "@/types/dto/category.dto";
import MiniFooter from "@/components/common/mini-footer/mini-footer";
import { CHAT_V2_CONFIG, LISTING_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Tab } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import Skeleton from "react-loading-skeleton";
import CategoryHeroSection from "./components/hero-section";
import BuyersGuideTab from "./tabs/buyers-guide-tab";
import CommunityTab from "./tabs/community-tab";
import ProductsTab from "./tabs/products-tab";
import UpcomingEvents from "../events/components/upcoming-events";
import { CommunityProvider } from "@/providers/community-provider";
import { ModeratorProvider } from "@/providers/moderator-provider";
import CategoryLatestSection from "./components/latest-section";
import Image from "next/image";
import { useModal } from "@/providers/modal-provider";
import EditorsPick from "./components/editors-pick";
import { ModalType } from "@/types/modal-type";
import StickyBottomAd from "./components/sticky-bottom-ad";

const stickyBottomAdData = {
	productName: "Sell.do",
	productLogo: "/images/listing-page/sell-do-product-logo.webp",
	productTagline: "Market Smarter. Sell Faster.",
	description: "Turn every lead into a sale with India's #1 Real Estate CRM",
	demoLink: "https://www.sell.do/contact-us",
	popupImage: "/images/listing-page/real-estate-crm-category-popup.jpg",
	popupVideoLink: "https://www.youtube-nocookie.com/embed/jIBzzDVJGzY?&autoplay=1",
};

const categorySlugToShowStickyBottomAdOn = "real-estate-crm-portal";

async function getCategoryData(slug: string) {
	const url = new URL(`${LISTING_API}/${slug}`);
	const res = await fetch(url);

	if (!res.ok) {
		const { message } = await res.json();
		throw new Error(message);
	}

	const { data } = (await res.json()) as { data: TGetCategoryDataResponseDTO };

	return data;
}

const Category = () => {
	const { slug } = useParams() as { slug: string };
	const { openModal } = useModal();

	// TO BE REMOVED: Temporary implementation for sales demo
	const showCategoryViewForSalesDemo = slug === "visitor-management-v2";

	const { isLoading, isError, data } = useQuery({
		queryKey: ["categoryData"],
		queryFn: () => getCategoryData(slug),
	});

	if (isError) {
		return (
			<div className="flex h-screen w-screen items-center justify-center">
				<span className="text-2xl font-medium text-gray-600">Something went wrong</span>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="flex flex-col gap-8">
				<Skeleton count={1} className="h-[512px] w-full" />
				<div className="mx-auto mt-8 w-full max-w-6xl">
					<div className="flex flex-col gap-8">
						<Skeleton count={1} className="h-48 w-full" />
						<Skeleton count={1} className="h-96 w-full" />
					</div>
				</div>
			</div>
		);
	}

	const isLatestMediaSectionVisible =
		(data?.categoryLatestNews && data.categoryLatestNews.length > 0) ||
		(data?.categoryLatestVideos && data.categoryLatestVideos.length > 0);

	const tabs: { label: string; content: React.ReactNode }[] = [];

	if (data?.products && data.products.length > 0) {
		tabs.push({
			label: "Products",
			content: (
				<>
					<ProductsTab products={data.products} filterRegions={data.productFilterRegions} />
					{isLatestMediaSectionVisible ? (
						<CategoryLatestSection
							videos={data?.categoryLatestVideos}
							news={data?.categoryLatestNews}
							title={data.title || ""}
						/>
					) : null}
					{showCategoryViewForSalesDemo ? (
						<div className="mt-12 flex flex-col gap-8">
							<UpcomingEvents />
						</div>
					) : null}
				</>
			),
		});
	}

	if (data?.communitySlug && data?.channelSlug) {
		tabs.push({
			label: "Community",
			content: (
				<CommunityProvider
					communityInfo={
						data?.communitySlug && data?.communityId
							? {
									id: data.communityId,
									name: data.communitySlug,
									slug: data.communitySlug,
									about: "",
									description: "",
									totalPosts: 0,
									totalViews: 0,
									totalMembers: 0,
									logo: "",
									coverImage: undefined,
									moderators: [],
									uniqueChatId: null,
									hasEvent: false,
									chatV2: CHAT_V2_CONFIG,
							  }
							: undefined
					}
					channelInfo={
						data?.channelSlug && data?.channelId && data?.communityId && data?.communitySlug
							? {
									id: data.channelId,
									name: data.channelSlug,
									slug: data.channelSlug,
									about: "",
									totalMembers: 0,
									totalViews: 0,
									totalPosts: 0,
									description: "",
									logo: "",
									community: {
										id: data.communityId,
										name: data.communitySlug,
										slug: data.communitySlug,
										logo: "",
									},
									coverImage: undefined,
									moderators: [],
									uniqueChatId: null,
									chatV2: CHAT_V2_CONFIG,
							  }
							: undefined
					}
				>
					<ModeratorProvider>
						<CommunityTab />
					</ModeratorProvider>
				</CommunityProvider>
			),
		});
	}

	if (!data?.is_knowledge_hub_hidden) {
		tabs.push({
			label: "Knowledge Hub / Buyers Guide",
			content: (
				<>
					<BuyersGuideTab
						title={data.title}
						content={data?.buyersGuide}
						faqs={data?.faqs}
						knowledgeHubResources={data.knowledgeHubResources}
					/>
					{isLatestMediaSectionVisible ? (
						<CategoryLatestSection
							videos={data?.categoryLatestVideos}
							news={data?.categoryLatestNews}
							title={data.title || ""}
						/>
					) : null}
					{showCategoryViewForSalesDemo ? (
						<div className="mt-12 flex flex-col gap-8">
							<UpcomingEvents />
						</div>
					) : null}
				</>
			),
		});
	}

	return (
		<div className="relative">
			<div className="listing-hero-clip-path absolute -z-10 h-full max-h-[938px] w-full bg-brand-primary/10  bg-[url('/images/listings/hero-section-bg.svg')] bg-cover bg-bottom" />

			<CategoryHeroSection
				name={data.title}
				description={data.description}
				logo={data.logoUrl}
				ambassadors={data.ambassadors}
				leaders={data?.leaders}
				features={data?.features}
				viewsCount={data.viewsCount}
				categorySlug={data.slug}
				channelMembers={data?.channelMembers}
				posts={data.posts}
				communityId={data?.communityId}
				channelId={data?.channelId}
				channelMembersCount={data.channelMembersCount}
				buyingGuideLink={data?.buyingGuideLink}
			/>

			<div className="mx-auto max-w-6xl px-4 pb-8 lg:px-0">
				{tabs.length > 0 && (
					<Tab.Group>
						<div className="mb-4 flex flex-col-reverse gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-4">
							<Tab.List className="no-scrollbar flex overflow-x-scroll lg:basis-2/3">
								{tabs.map((tab, i) => (
									<Tab
										key={i}
										className={({ selected }) =>
											cn(
												"flex-none cursor-pointer px-4 py-2 text-lg",
												selected
													? "border-b-4 border-brand-primary font-bold text-brand-primary outline-none"
													: "font-semibold text-black hover:bg-gray-100"
											)
										}
									>
										{tab.label}
									</Tab>
								))}
							</Tab.List>
						</div>

						<Tab.Panels>
							{tabs.map((tab, i) => (
								<Tab.Panel key={i}>{tab.content}</Tab.Panel>
							))}
						</Tab.Panels>
					</Tab.Group>
				)}
			</div>

			{data?.editorsPick && data.editorsPick.length > 0 && (
				<div className={cn(categorySlugToShowStickyBottomAdOn === data.slug && "pb-32")}>
					<EditorsPick editorsPick={data.editorsPick} />
				</div>
			)}
			{categorySlugToShowStickyBottomAdOn === data.slug ? (
				<StickyBottomAd {...stickyBottomAdData} />
			) : (
				<MiniFooter />
			)}

			<div onClick={() => openModal(ModalType.ContactUs)}>
				<Image
					src="/images/listing-page/contact-us-widget.png"
					alt="Contact Us"
					width={393}
					height={127}
					className={cn(
						"fixed left-3 z-50 h-[83px] w-[255px]",
						categorySlugToShowStickyBottomAdOn === data.slug ? "bottom-[25%]" : "bottom-[20%]"
					)}
				/>
			</div>
		</div>
	);
};

export default Category;
